import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CHANNEL_LAYOUT_VALUES } from 'wix-vod-shared/src/app-settings/constants/channel-layout-values';

import { videosPerPage } from 'widget/utils/videos-sizes';
import BIUnhandledErrorLogger from 'shared/bi/bi-unhandled-error-logger';

import { COMPACT_NUMBER_OF_ROWS, COMPACT_VIDEOS_IN_ROW } from 'shared/constants/compact-layout';

import { getChannelForWidget } from 'wix-vod-shared/src/common/selectors/channels';
import {
  getAllSettings,
  getNumberOfRows,
  getEditMode,
  getChannelLayout,
} from 'shared/selectors/app-settings';

import { getVideoIdFromUrl } from 'widget/selectors/query-params.js';
import { getSearchQuery, getSearchQueryField } from '../../../shared/selectors/search';
import { getMainVideoId } from 'wix-vod-shared/src/widget/ui-selectors/selected-video-id';
import { selectVideo } from 'widget/redux/client/actions/select-video';
import { getVideosGroupedByIds } from 'shared/selectors/videos';

import getExperiments from 'shared/redux/server/actions/experiments/get';
import TooltipPopout from 'shared/components/tooltip/tooltip-popout';
import Modal from 'shared/components/modal/modal';

import BiHandler from 'shared/bi/bi-handler';
import UpdateChannelPublishedState from 'widget/data-components/update-channel-published-state';
import ChannelLoader from 'widget/data-components/channel';
import PaymentInfoLoader from 'widget/data-components/payment-info';
import PaginatedChannelVideosLoader, { DATA_KEY as PAGINATED_VIDEOS_KEY } from 'widget/data-components/paginated-channel-videos';
import NextChannelVideosLoader from 'widget/data-components/next-channel-videos';
import ChannelVideoLoader from 'widget/data-components/channel-video';
import CurrentSiteUser from 'widget/data-components/current-site-user';
import ReloadChannelPaymentDataOnPayment from 'widget/data-components/reload-channel-payment-data-on-payment';
import ClearVideoUrlsCacheOnPayment from 'widget/data-components/clear-video-urls-cache-on-payment';
import SetInitialWidgetSize from 'widget/data-components/set-initial-widget-size';
import PaymentEventProxy from 'shared/components/payment-events/proxy';
import PaymentsBiHandler from 'widget/components/payments/bi-handler';
import PremiumIntentUpdater from 'widget/components/premium-intent-updater/premium-intent-updater';

import 'shared/styles/core.styl';


@connect(state => ({
    appSettings: getAllSettings(state),
    selectedVideoId: state.selectedVideoId,
    channelData: getChannelForWidget(state),
    currentChannelId: state.currentChannelId,
    mainVideoId: getMainVideoId(state, PAGINATED_VIDEOS_KEY),
    numberOfRows: getNumberOfRows(state),
    videoIdFromUrl: getVideoIdFromUrl(state),
    searchQuery: getSearchQuery(state),
    searchQueryField: getSearchQueryField(state),
    editMode: getEditMode(state),
    layout: getChannelLayout(state),
    videos: getVideosGroupedByIds(state),
  }),
  {
    selectVideo,
    getExperiments,
  }
)
export default class DesktopLayout extends React.Component {

  static propTypes = {
    selectedVideoId: PropTypes.string,
    children: PropTypes.element,
    currentChannelId: PropTypes.string,
    mainVideoId: PropTypes.string,
    videoIdFromUrl: PropTypes.string,
    channelData: PropTypes.object,
    appSettings: PropTypes.object,
    selectVideo: PropTypes.func,
    getExperiments: PropTypes.func,
    numberOfRows: PropTypes.number,
    searchQuery: PropTypes.string,
    searchQueryField: PropTypes.string,
    editMode: PropTypes.string,
    layout: PropTypes.number,
    videos: PropTypes.object,
  };

  componentDidMount() {
    // TODO: remove experiments fetching after opening video embed to all users
    this.props.getExperiments();
  }

  componentWillUpdate(nextProps) {
    const { videoIdFromUrl, selectedVideoId, videos, channelData } = nextProps;

    if (_.isEmpty(channelData) || _.isEmpty(videos[videoIdFromUrl])) {
      return;
    }

    if (videoIdFromUrl && !selectedVideoId) {
      this.props.selectVideo(videoIdFromUrl);
    }
  }

  // Todo: move to loader or reducer
  get videosPerPage() {
    const { appSettings, numberOfRows, channelData } = this.props;
    const { videosInRow, channelLayout } = appSettings.numbers;
    const isStrip = channelLayout === CHANNEL_LAYOUT_VALUES.STRIP;
    const isCompact = channelLayout === CHANNEL_LAYOUT_VALUES.COMPACT;

    if (isCompact) {
      return COMPACT_NUMBER_OF_ROWS * COMPACT_VIDEOS_IN_ROW;
    }

    if (isStrip) {
      return videosInRow * numberOfRows;
    }

    const allVideosCount = channelData && channelData.videosCount;

    return videosPerPage(
      appSettings.requestedWidgetSize,
      videosInRow,
      allVideosCount,
      appSettings,
      channelData || {},
    );
  }

  renderChildren() {
    const { channelData, children } = this.props;

    if (_.isEmpty(channelData)) {
      return null;
    }

    return children;
  }

  get content() {
    const {
      appSettings,
      mainVideoId,
      videoIdFromUrl,
      currentChannelId,
      selectedVideoId,
    } = this.props;

    if (!appSettings || !currentChannelId) {
      return null;
    }

    const { searchQuery, searchQueryField } = this.props;
    const nextChannelVideosVideoId = selectedVideoId || videoIdFromUrl || mainVideoId;

    return (
      <ChannelLoader channelId={currentChannelId}>
        <PaginatedChannelVideosLoader channelId={currentChannelId}
                                      videosPerUIPage={this.videosPerPage}
                                      searchQuery={searchQuery}
                                      searchQueryField={searchQueryField}
                                      as={PAGINATED_VIDEOS_KEY}>
          <ChannelVideoLoader channelId={currentChannelId}
                              videoId={videoIdFromUrl}>
            <NextChannelVideosLoader channelId={currentChannelId}
                                     currentVideoId={nextChannelVideosVideoId}>
              <CurrentSiteUser>
                <PaymentInfoLoader channelId={currentChannelId}>
                  {this.renderChildren()}
                  <PaymentEventProxy />
                  <ReloadChannelPaymentDataOnPayment />
                  <ClearVideoUrlsCacheOnPayment />
                </PaymentInfoLoader>
              </CurrentSiteUser>
            </NextChannelVideosLoader>
          </ChannelVideoLoader>
        </PaginatedChannelVideosLoader>
      </ChannelLoader>
    );
  }

  render() {
    const { currentChannelId, appSettings, editMode, layout } = this.props;

    return (
      <div data-layout={layout}
           data-view-mode={editMode}>
        <SetInitialWidgetSize />
        <BiHandler />
        <BIUnhandledErrorLogger />
        <PaymentsBiHandler />
        <UpdateChannelPublishedState channelId={currentChannelId} />
        {this.content}

        <Modal settings={appSettings} />
        <TooltipPopout />
        <PremiumIntentUpdater />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/layouts/desktop/desktop.js